// `(rename ds :old :new)` — relabel a single column. Top-level eager op
// — multi-rename composes via threading:
//   `(-> ds (rename :a :A) (rename :b :B))`.

import { z } from 'zod';

import { nodePos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type PropagateSchemaCtx, type SpecialCtx } from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { runtimeRename } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { type ColumnName, ColumnNameSchema, columnNameString } from '../../schema/keyword.ts';
import { type Schema, schemaFromEntries } from '../../schema/types.ts';
import type { MaterializedDataset, Value } from '../../value.ts';
import { columnNameAstName, requireColumns, resolveDsArg } from './helpers.ts';

/**
 * `(rename ds :old :new)` — produce a new dataset with column `:old`
 * relabelled to `:new`. Each name is a keyword (typical) or a string
 * (special-character fallback). Multi-rename composes via threading.
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - `:old` isn't a column on `ds.schema` → `UnknownColumnRefError`
 *     (with the dataset's columns as the "did you mean").
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before the
 * arquero `rename` runs — `rename` produces a `MaterializedDataset`
 * either way.
 */
export const rename = z
  .object({
    name: z.literal('rename'),
    params: z.tuple([astArg('<dataset>'), ColumnNameSchema, ColumnNameSchema]),
  })
  .describe(
    'Rename a single column. Names are keywords (typical) or strings (arbitrary characters). ' +
      'Multi-rename composes via threading: `(-> ds (rename :a :A) (rename :b :B))`.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(-> wells (rename :revenue :amount))' },
    apply: async (data, ctx: SpecialCtx): Promise<MaterializedDataset> => {
      const [dsRaw, oldRaw, newRaw] = data.params as readonly [Value, ColumnName, ColumnName];
      const concrete = await resolveDsArg(dsRaw, '(rename ds :old :new)', ctx);
      const oldName = columnNameString(oldRaw);
      const newName = columnNameString(newRaw);
      requireColumns([oldName], concrete.schema, () => nodePos(ctx.spans, paramPath(ctx.path, 1)));
      // Boundary rename — carries a boxed column's unit companion along.
      return runtimeRename(concrete, { [oldName]: newName });
    },
    dataset: {
      // Relabel `:old` to `:new`, preserving column order and types.
      // Malformed names (apply-time errors) leave the schema unchanged
      // here; the runtime path raises the actual diagnostic.
      propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
        const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
        const oldName = columnNameAstName(params[1]);
        const newName = columnNameAstName(params[2]);
        if (oldName === undefined || newName === undefined) {
          return ctx.inputSchema;
        }
        return schemaFromEntries(
          Array.from(ctx.inputSchema.entries(), ([k, v]) => [k === oldName ? newName : k, v] as const),
        );
      },
      // The OLD name is the only column USAGE — `:new` is a definition
      // (target label), excluded from refs per `ColumnRefInArg`'s
      // contract.
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
        const oldName = columnNameAstName(params[1]);
        return oldName === undefined ? [] : [{ name: oldName, path: paramPath(basePath, 1) }];
      },
    },
  });
